import {
  Button,
  CircularProgress,
  MenuItem,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { useMemo, useState } from "react";
import BoostDialog from "./BoostDialog";
import CreateAdButton from "./CreateAdButton";
import PostCard from "./PostCard";
import { apiErrorMessage } from "./marketingFormat";
import { useMarketingActions, usePosts } from "./useMarketing";

export default function PostsPanel({ connections, perms, onOpenCampaign }) {
  const [chosenId, setChosenId] = useState(null);
  const [chosenSource, setChosenSource] = useState("FACEBOOK");
  const [boosting, setBoosting] = useState(null);
  const connection = connections.find((c) => c.id === chosenId) || connections[0];
  const hasInstagram = Boolean(connection?.instagramUserId);
  const source = chosenSource === "INSTAGRAM" && !hasInstagram ? "FACEBOOK" : chosenSource;
  const posts = usePosts(connection?.id, source);
  const { boost } = useMarketingActions();
  const rows = useMemo(
    () => (posts.data?.pages || []).flatMap((page) => page?.posts || []),
    [posts.data]
  );

  if (!connection) {
    return <p className="text-sm text-app-text-secondary">Connect a Meta account to see posts.</p>;
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        {connections.length > 1 ? (
          <TextField
            select
            size="small"
            label="Account"
            value={connection.id}
            onChange={(e) => setChosenId(e.target.value)}
            sx={{ minWidth: 200 }}
          >
            {connections.map((item) => (
              <MenuItem key={item.id} value={item.id}>
                {item.label}
              </MenuItem>
            ))}
          </TextField>
        ) : null}
        <ToggleButtonGroup
          exclusive
          size="small"
          value={source}
          onChange={(_, next) => next && setChosenSource(next)}
          aria-label="Post source"
        >
          <ToggleButton value="FACEBOOK">Facebook page</ToggleButton>
          <ToggleButton value="INSTAGRAM" disabled={!hasInstagram}>
            Instagram
          </ToggleButton>
        </ToggleButtonGroup>
        {perms.canCreate ? (
          <CreateAdButton connections={connections} defaultConnectionId={connection.id} />
        ) : null}
      </div>
      {!hasInstagram ? (
        <p className="text-xs text-app-text-muted">
          Add an Instagram user id to this account under Connection to browse and boost Instagram
          posts.
        </p>
      ) : null}

      {posts.isLoading ? <CircularProgress size={24} /> : null}
      {posts.isError ? (
        <div className="rounded border border-app-border p-4 text-sm">
          <p className="font-medium text-app-status-cancelled-text">
            {apiErrorMessage(posts.error, "Could not load posts from Meta.")}
          </p>
          <p className="mt-1 text-app-text-secondary">
            Run Test connection under Connection to check the token has the page permissions it
            needs.
          </p>
          <Button size="small" onClick={() => posts.refetch()}>
            Try again
          </Button>
        </div>
      ) : null}
      {!posts.isLoading && !posts.isError && rows.length === 0 ? (
        <p className="text-sm text-app-text-secondary">No posts found.</p>
      ) : null}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {rows.map((post) => (
          <PostCard
            key={post.id}
            post={post}
            canBoost={perms.canCreate}
            onBoost={setBoosting}
            onOpenBoost={onOpenCampaign}
          />
        ))}
      </div>
      {posts.hasNextPage ? (
        <Button
          size="small"
          disabled={posts.isFetchingNextPage}
          onClick={() => posts.fetchNextPage()}
        >
          Load more
        </Button>
      ) : null}

      {boosting ? (
        <BoostDialog
          post={boosting}
          connection={connection}
          mutation={boost}
          onClose={() => setBoosting(null)}
        />
      ) : null}
    </div>
  );
}
